import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import Routes from '../../data/routes'
import sessionUtils from '../../data/session-utils'
import useCart from '../../data/useCart'
import useCheckoutContext from '../../data/useCheckoutContext'
import useOrder from '../../data/useOrder'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import mapCheckoutDataToCheckoutStepsFlags from '../../lib/map-checkout-data-to-checkout-steps-flags'
import Cart from '../../typings/Cart'
import Order from '../../typings/Order'

import { StepperCartDocument, StepperCartQuery, StepperOrderDocument, StepperOrderQuery } from '../../graphql'
import StepperComponent, { Step } from './StepperComponent'

enum VisualSteps {
  CHOOSE_VIDEO = 'choose-video',
  CHOOSE_ACTIVITY = 'choose-activity',
  CHOOSE_PRODUCT = 'choose-product',
  REVIEW_INFORMATION = 'review-information',
  ADDITIONAL_INFORMATION = 'additional-information',
  PAYMENT = 'payment',
  CONFIRMATION = 'confirmation',
}

const StepsMapping = {
  [VisualSteps.CHOOSE_VIDEO]: { labelKey: 'step-choose-video', path: '/choose-video' },
  [VisualSteps.CHOOSE_ACTIVITY]: { labelKey: 'step-choose-activity', path: '/choose-activity' },
  [VisualSteps.CHOOSE_PRODUCT]: { labelKey: 'step-choose-product', path: Routes.CHOOSE_PRODUCT },
  [VisualSteps.ADDITIONAL_INFORMATION]: {
    labelKey: 'step-additional-information',
    path: Routes.ADDITIONAL_INFORMATION,
  },
  [VisualSteps.REVIEW_INFORMATION]: {
    labelKey: 'step-review-information',
    path: Routes.REVIEW_INFORMATION,
  },
  [VisualSteps.PAYMENT]: { labelKey: 'step-payment', path: Routes.PAYMENT },
  [VisualSteps.CONFIRMATION]: { labelKey: 'step-confirmation', path: Routes.CONFIRMATION },
}

const calculateVisualSteps = (data: Cart | Order): Array<VisualSteps> => {
  const visualSteps: Array<VisualSteps> = []
  const { isBookableItemCart, hasAdditionalInformation, hasSomethingToPay, isOrderPaid, hasBufferPage } =
    mapCheckoutDataToCheckoutStepsFlags(data)

  if (data.__typename === 'Cart') {
    if (isBookableItemCart) {
      visualSteps.push(VisualSteps.CHOOSE_ACTIVITY)
    }

    const isProductARecording =
      data.items &&
      data.items.length > 0 &&
      data.items[0].__typename === 'ProductCartItem' &&
      data.items[0].product &&
      data.items[0].product.__typename === 'Recording'

    if (isProductARecording) {
      visualSteps.push(VisualSteps.CHOOSE_VIDEO)
    }

    visualSteps.push(VisualSteps.CHOOSE_PRODUCT)

    if (hasBufferPage) {
      visualSteps.push(VisualSteps.REVIEW_INFORMATION)
    }

    if (hasAdditionalInformation) {
      visualSteps.push(VisualSteps.ADDITIONAL_INFORMATION)
    }

    if (hasSomethingToPay) {
      visualSteps.push(VisualSteps.PAYMENT)
    }
  } else if (data.__typename === 'Order') {
    if (hasSomethingToPay || !isOrderPaid) {
      visualSteps.push(VisualSteps.PAYMENT)
    }
  }

  visualSteps.push(VisualSteps.CONFIRMATION)

  return visualSteps
}

const Stepper: React.FC<React.PropsWithChildren<unknown>> = () => {
  const location = useLocation()
  const context = useCheckoutContext()
  const [mappedSteps, setMappedSteps] = useState<Array<Step>>([])
  const { cartId, orderId, paymentInProgress } = getPhoenixUrlParams(location.search)

  const { cartData, isCartLoading } = useCart<StepperCartQuery>(StepperCartDocument, {
    skip: !cartId,
    nextFetchPolicy: 'cache-only',
  })

  const { orderData, isOrderLoading } = useOrder<StepperOrderQuery>(StepperOrderDocument, {
    skip: !orderId,
    nextFetchPolicy: 'cache-only',
  })

  const data = useMemo(() => (cartData && cartData.cart) || (orderData && orderData.order), [cartData, orderData])

  const calculateAndMapSteps = useCallback((stepData: Cart | Order): Array<Step> => {
    const visualSteps = calculateVisualSteps(stepData)
    return visualSteps.map((step) => StepsMapping[step])
  }, [])

  const stepsAreEqual = useCallback((steps1: Array<Step>, steps2: Array<Step>): boolean => {
    if (steps1.length !== steps2.length) return false
    return steps1.every((step, index) => step.path === steps2[index].path && step.labelKey === steps2[index].labelKey)
  }, [])

  useEffect(() => {
    if (!data) return

    // Handle payment in progress case
    if (paymentInProgress && mappedSteps.length === 0) {
      const sessionStorageStepper = sessionUtils.getStepper()
      if (sessionStorageStepper) {
        setMappedSteps(sessionStorageStepper)
        return
      }
    }

    // Calculate new steps
    const newMappedSteps = calculateAndMapSteps(data)

    // Only update if steps have changed or if we don't have any steps yet
    if (mappedSteps.length === 0 || !stepsAreEqual(mappedSteps, newMappedSteps)) {
      sessionUtils.setStepper(newMappedSteps)
      setMappedSteps(newMappedSteps)
    }
  }, [data, context.steps, paymentInProgress, mappedSteps, calculateAndMapSteps, stepsAreEqual])

  const isLoading = useMemo(
    () => isCartLoading || isOrderLoading || mappedSteps.length === 0,
    [isCartLoading, isOrderLoading, mappedSteps.length],
  )

  return <StepperComponent steps={mappedSteps} isLoading={isLoading} />
}

export default Stepper
